import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';

const VERSIONES = ['6.45.x','6.46.x','6.47.x','6.48.x','6.49.x','7.x.x'];
const TIPOS = ['Administración','Borde','Otro'];
const MODOS_IP = ['DHCP','PPPoE','IP Fija'];
const GESTIONES = ['Simple Queue','PPPoE','Queue Tree'];

const MODELOS_MIKROTIK = [
    'RB450Gx4','RB750G','RB750r2','RB750Gr3','RB750UPr2','RB750P-PBr2',
    'RB760iGS','RB960PGS','RB960PGS-PB','L009UiGS-RM',
    'RB3011UiAS-RM','RB4011iGS+RM','RB1100AHx4','RB1100AHx2','RB1200',
    'RB1100AH','RB1100Hx2','RB1100','RB1000','RB1000U',
    'RB2011L','RB2011LS','RB2011UiAS-2HnD','RB2011iL-IN','RB2011iL-RM',
    'RB2011iLS-IN','RB2011UiAS-IN','RB2011UiAS-RM','RB2011UiAS-IN',
    'RB1100AHx2-LM','CCR1009-8G-1S','CCR1009-8G-1S-PC','CCR1009-7G-1C-PC',
    'CCR1009-8G-1S-1S+','CCR1009-8G-1S-1S+PC','CCR1009-7G-1C-1S+',
    'CCR1009-7G-1C-1S+PC','CCR1016-12G-BU','CCR1016-12G','CCR1016-12S-1S+',
    'CCR1036-12G-4S','CCR1036-8G-2S+','CCR1036-12G-4S-EM','CCR1036-8G-2S+EM',
    'CCR1072-1G-8S+','CCR2004-1G-2XS-PCIe','CCR2004-16G-2S+','CCR2116-12G-4S+',
    'RB5009UG+S+IN','RB5009UPr+S+IN','RB5009UPr+S+OUT'
];

const EMPTY_FORM = {
    etiqueta: '',
    nodo_id: '',
    modelo: '',
    version: '7.x.x',
    tipo: 'Administración',
    modo_ip: 'IP Fija',
    gestion_velocidad: 'Simple Queue',
    ip: '',
    usuario: '',
    clave: '',
    puerto_api: 8728,
    activo: true
};

const Mikrotik = () => {
    const { can, user } = useAuth();
    const [items, setItems] = useState([]);
    const [nodos, setNodos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);
    const [conexion, setConexion] = useState(null);
    const [redesSeleccionadas, setRedesSeleccionadas] = useState([]);
    const [importando, setImportando] = useState(false);
    const [search, setSearch] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [mkRes, nodosRes] = await Promise.all([
                api.get('/infraestructura/mikrotik'),
                api.get('/infraestructura/nodos')
            ]);
            setItems(mkRes.data);
            setNodos(nodosRes.data);
        } catch {
            setError('Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) loadData(); }, [loadData, user]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setError('');
        setConexion(null);
        setRedesSeleccionadas([]);
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setForm({
            etiqueta: item.etiqueta || '',
            nodo_id: item.nodo_id || '',
            modelo: item.modelo || '',
            version: item.version || '7.x.x',
            tipo: item.tipo || 'Administración',
            modo_ip: item.modo_ip || 'IP Fija',
            gestion_velocidad: item.gestion_velocidad || 'Simple Queue',
            ip: item.ip || '',
            usuario: item.usuario || '',
            clave: '',
            puerto_api: item.puerto_api || 8728,
            activo: !!Number(item.activo)
        });
        setEditingId(item.id);
        setError('');
        setConexion(null);
        setRedesSeleccionadas([]);
        setModalOpen(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError('');
        setConexion(null);
        setRedesSeleccionadas([]);
        try {
            let res;
            if (editingId) {
                res = await api.put(`/infraestructura/mikrotik/${editingId}`, form);
                showSuccess('MikroTik actualizada correctamente');
            } else {
                res = await api.post('/infraestructura/mikrotik', form);
                showSuccess('MikroTik conectada correctamente');
            }
            if (res.data?.conexion) {
                setConexion(res.data.conexion);
                // Pre-seleccionar todas las redes disponibles
                if (res.data.conexion.redes?.length > 0) {
                    setRedesSeleccionadas(res.data.conexion.redes.map((_, i) => i));
                }
            } else {
                setModalOpen(false);
            }
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando MikroTik');
        } finally {
            setSaving(false);
        }
    };

    const toggleRed = (index) => {
        setRedesSeleccionadas(prev =>
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const handleImportarPools = async () => {
        if (redesSeleccionadas.length === 0) return;
        setImportando(true);
        const mikrotikId = editingId || items[items.length - 1]?.id;
        const redes = conexion.redes.filter((_, i) => redesSeleccionadas.includes(i));
        try {
            await api.post(`/infraestructura/mikrotik/${mikrotikId}/importar-pools`, { redes });
            showSuccess(`${redes.length} IP Pool(s) importados correctamente`);
            setModalOpen(false);
            setConexion(null);
            setRedesSeleccionadas([]);
        } catch (err) {
            setError(err.response?.data?.error || 'Error importando IP Pools');
        } finally {
            setImportando(false);
        }
    };

    const filtered = items.filter(i =>
        `${i.etiqueta} ${i.ip} ${i.modelo} ${i.nodo_nombre || ''}`.toLowerCase().includes(search.toLowerCase())
    );

    const inputClass = "w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Equipos MikroTik</h2>
                {can('inf_mikrotik', 'create') && (
                    <button onClick={openCreate} className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg" style={{ backgroundColor: 'var(--color-primary)' }}>
                        + Conectar MikroTik
                    </button>
                )}
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && !modalOpen && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            <div className="mb-4">
                <input type="text" placeholder="Buscar MikroTik..." value={search} onChange={e => setSearch(e.target.value)}
                    className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
            </div>

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Cargando...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No se encontraron equipos</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Etiqueta</th>
                                <th className="px-4 py-3 text-left">Modelo</th>
                                <th className="px-4 py-3 text-left">IP</th>
                                <th className="px-4 py-3 text-left">Puerto API</th>
                                <th className="px-4 py-3 text-left">Versión</th>
                                <th className="px-4 py-3 text-left">Gestión</th>
                                <th className="px-4 py-3 text-left">Nodo</th>
                                {can('inf_mikrotik', 'update') && <th className="px-4 py-3 text-right">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-gray-800">{item.etiqueta}</td>
                                    <td className="px-4 py-3 text-gray-600">{item.modelo || '—'}</td>
                                    <td className="px-4 py-3 text-gray-600 font-mono">{item.ip}</td>
                                    <td className="px-4 py-3 text-gray-500">{item.puerto_api}</td>
                                    <td className="px-4 py-3 text-gray-500">{item.version || '—'}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{item.gestion_velocidad}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{item.nodo_nombre || '—'}</td>
                                    {can('inf_mikrotik', 'update') && (
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => openEdit(item)}
                                                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
                                                Editar
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">{editingId ? 'Editar MikroTik' : 'Conectar MikroTik'}</h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Etiqueta / Descripción *</label>
                                    <input name="etiqueta" value={form.etiqueta} onChange={handleChange} className={inputClass} placeholder="Ej: CCR1036_BOLO" />
                                </div>
                                <div>
                                    <label className={labelClass}>Nodo</label>
                                    <select name="nodo_id" value={form.nodo_id} onChange={handleChange} className={inputClass}>
                                        <option value="">Sin nodo</option>
                                        {nodos.map(n => <option key={n.id} value={n.id}>{n.nombre}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Modelo</label>
                                    <select name="modelo" value={form.modelo} onChange={handleChange} className={inputClass}>
                                        <option value="">Seleccionar...</option>
                                        {MODELOS_MIKROTIK.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Versión</label>
                                    <select name="version" value={form.version} onChange={handleChange} className={inputClass}>
                                        {VERSIONES.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className={labelClass}>Tipo</label>
                                    <select name="tipo" value={form.tipo} onChange={handleChange} className={inputClass}>
                                        {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Modo IP</label>
                                    <select name="modo_ip" value={form.modo_ip} onChange={handleChange} className={inputClass}>
                                        {MODOS_IP.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Gestión velocidad</label>
                                    <select name="gestion_velocidad" value={form.gestion_velocidad} onChange={handleChange} className={inputClass}>
                                        {GESTIONES.map(g => <option key={g} value={g}>{g}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>IP *</label>
                                    <input name="ip" value={form.ip} onChange={handleChange} className={inputClass} placeholder="Ej: 10.85.94.177" />
                                </div>
                                <div>
                                    <label className={labelClass}>Puerto API</label>
                                    <input name="puerto_api" type="number" value={form.puerto_api} onChange={handleChange} className={inputClass} />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Usuario</label>
                                    <input name="usuario" value={form.usuario} onChange={handleChange} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Contraseña</label>
                                    <input name="clave" type="password" value={form.clave} onChange={handleChange} className={inputClass} placeholder={editingId ? "Dejar vacío para no cambiar" : ""} />
                                </div>
                            </div>

                            {editingId && (
                                <div className="flex items-center gap-2">
                                    <input type="checkbox" name="activo" checked={form.activo} onChange={handleChange} id="activo_mk" />
                                    <label htmlFor="activo_mk" className="text-sm text-gray-700">MikroTik activa</label>
                                </div>
                            )}

                            {/* Resultado de conexión */}
                            {conexion && (
                                <div className="space-y-3">
                                    <div className={`rounded-lg px-4 py-3 text-sm border ${
                                        conexion.conectado
                                            ? 'bg-green-50 border-green-200 text-green-700'
                                            : 'bg-red-50 border-red-200 text-red-600'
                                    }`}>
                                        <p className="font-semibold mb-1">{conexion.conectado ? '✅ Conexión exitosa' : '❌ Error de conexión'}</p>
                                        <p>{conexion.mensaje}</p>
                                        {conexion.conectado && (
                                            <p className="text-xs mt-1 text-green-600">Identity: {conexion.identity} | RouterOS {conexion.version}</p>
                                        )}
                                    </div>

                                    {conexion.conectado && conexion.redes?.length > 0 && (
                                        <div className="border border-gray-200 rounded-lg p-4">
                                            <p className="text-sm font-semibold text-gray-700 mb-3">
                                                Redes disponibles para importar como IP Pool:
                                            </p>
                                            <div className="space-y-2">
                                                {conexion.redes.map((red, i) => (
                                                    <label key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={redesSeleccionadas.includes(i)}
                                                            onChange={() => toggleRed(i)}
                                                            className="w-4 h-4"
                                                        />
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-800">
                                                                {red.interface} — {red.subnet}/{red.prefijo}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                Gateway: {red.gateway} | {red.ip_inicio} — {red.ip_fin}
                                                            </p>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {conexion.conectado && conexion.redes?.length === 0 && (
                                        <p className="text-xs text-gray-500">No se encontraron redes de clientes adicionales en este router.</p>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => { setModalOpen(false); setConexion(null); setRedesSeleccionadas([]); }}
                                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">
                                {conexion ? 'Cerrar' : 'Cancelar'}
                            </button>
                            {!conexion && (
                                <button onClick={handleSubmit} disabled={saving}
                                    className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                    style={{ backgroundColor: 'var(--color-primary)' }}>
                                    {saving ? 'Conectando...' : (editingId ? 'Guardar cambios' : 'Conectar MikroTik')}
                                </button>
                            )}
                            {conexion?.conectado && conexion.redes?.length > 0 && redesSeleccionadas.length > 0 && (
                                <button onClick={handleImportarPools} disabled={importando}
                                    className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                    style={{ backgroundColor: 'var(--color-primary)' }}>
                                    {importando ? 'Importando...' : `Importar ${redesSeleccionadas.length} red(es)`}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Mikrotik;
